import { useState, useEffect } from 'react';
import { X, Search, Loader2, ShoppingCart, AlertTriangle } from 'lucide-react';
import { api } from '../../../api/client';
import { Can } from '../../../lib/rbac';

interface PendingPurchase {
  id: number;
  amazon_order_id: string | null;
  link_source: string | null;
  amazon_status: string;
  tracking_number: string | null;
  created_at: string;
}

interface PendingSale {
  order_id: number;
  ml_order_id: string;
  seller_id: number;
  seller_nickname: string | null;
  buyer_nickname: string | null;
  buyer_name: string | null;
  date_created: string;
  context_site: string | null;
  item_title: string | null;
  quantity: number;
  ml_shipment_id: number | null;
  ship_status: string | null;
  ship_substatus: string | null;
  ship_deadline: string | null;
  claim_open: boolean;
  purchases: PendingPurchase[];
}

export interface PendingSalesModalProps {
  asin: string;
  /** Orden Amazon (del reporte) de la unidad que está en la mesa. */
  amazonOrderId: string;
  productTitle?: string | null;
  crossAll?: boolean;
  onClose: () => void;
  /** Reasignación hecha: el padre re-escanea por nº de orden ML y abre el popup clásico. */
  onReassigned: (mlOrderId: string) => void;
}

const SHIP_LABEL: Record<string, string> = {
  ready_to_ship: 'Lista para despachar',
  handling: 'ML armando el envío',
  pending: 'Envío pendiente',
};

const PURCHASE_SOURCE: Record<string, string> = {
  manual: 'a mano',
  api: 'automática',
  warehouse_scan: 'desde depósito',
};

function fmtDate(v: string | null | undefined) {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
}

export function PendingSalesModal({
  asin,
  amazonOrderId,
  productTitle,
  crossAll,
  onClose,
  onReassigned,
}: PendingSalesModalProps) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<PendingSale[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(
        `/warehouse/leftover/search-pending-sales?asin=${encodeURIComponent(asin)}` +
          (crossAll ? '&cross_all=true' : '') +
          (q.trim() ? `&q=${encodeURIComponent(q.trim())}` : '')
      )
      .then((res: any) => {
        if (!cancelled) setResults(res.success ? res.results : []);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [asin, q, crossAll]);

  const reassign = async (sale: PendingSale) => {
    setBusyId(sale.order_id);
    setError(null);
    try {
      const res: any = await api.post('/warehouse/leftover/reassign-purchase', {
        order_id: sale.order_id,
        amazon_order_id: amazonOrderId,
        asin,
        cross_all: !!crossAll,
      });
      if (!res.success) throw new Error(res.error || 'Error al reasignar la compra');
      onReassigned(String(res.ml_order_id || sale.ml_order_id));
    } catch (err: any) {
      setError(err?.message || 'Error al reasignar la compra');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[var(--color-bg-card)] rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-4 py-3 border-b border-[var(--color-border)] gap-3">
          <div className="min-w-0">
            <p className="font-bold text-[var(--color-text)]">Elegir venta para esta unidad</p>
            <p className="text-xs text-[var(--color-text-secondary)] truncate">
              {productTitle || asin} · orden Amazon {amazonOrderId}
            </p>
          </div>
          <button onClick={onClose} className="shrink-0">
            <X size={20} className="text-[var(--color-text-secondary)]" />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="flex items-center gap-2 border border-[var(--color-border)] rounded-lg px-3 py-2">
            <Search size={16} className="text-[var(--color-text-secondary)]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filtrar por comprador o nº de orden (opcional)"
              className="flex-1 bg-transparent outline-none text-sm text-[var(--color-text)]"
            />
          </div>
          <p className="text-xs text-[var(--color-text-secondary)]">
            Ventas pagas de este producto que todavía no pasaron por el depósito (más vieja arriba).
            Al elegir una, esta unidad pasa a ser su compra; si la venta ya tenía otra compra, esa
            queda marcada como reemplazada.
          </p>
          {error && <p className="text-sm text-red-500">{error}</p>}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-[var(--color-text-secondary)]" />
            </div>
          ) : results.length === 0 ? (
            <p className="text-sm text-[var(--color-text-secondary)] text-center py-8">
              No hay ventas pendientes de este producto.
            </p>
          ) : (
            results.map((o) => {
              const shipTxt = o.ship_status
                ? SHIP_LABEL[o.ship_status] || o.ship_status
                : 'sin envío todavía';
              return (
                <div
                  key={o.order_id}
                  className={`rounded-xl border p-3 space-y-2 bg-[var(--color-bg)] ${
                    o.claim_open ? 'border-orange-400' : 'border-[var(--color-border)]'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-0.5">
                      <p className="text-sm font-bold text-[var(--color-text)]">
                        {o.ml_order_id}
                        {o.context_site && (
                          <span className="ml-2 text-xs font-medium px-1.5 py-0.5 rounded bg-[var(--color-surface)] text-[var(--color-text-secondary)]">
                            {o.context_site.toUpperCase()}
                          </span>
                        )}
                        {o.seller_nickname && (
                          <span className="ml-2 text-xs text-[var(--color-text-secondary)]">
                            {o.seller_nickname}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-[var(--color-text-secondary)]">
                        {o.buyer_name || o.buyer_nickname || 'sin comprador'} · vendida el{' '}
                        {fmtDate(o.date_created)} · {o.quantity} unidad(es)
                      </p>
                      <p className="text-xs text-[var(--color-text-secondary)]">
                        Envío ML: {shipTxt}
                        {o.ship_substatus ? ` (${o.ship_substatus})` : ''}
                        {o.ship_deadline ? ` · límite ${fmtDate(o.ship_deadline)}` : ''}
                      </p>
                      {o.claim_open && (
                        <p className="text-xs text-orange-600 flex items-center gap-1">
                          <AlertTriangle size={12} /> Tiene un reclamo abierto
                        </p>
                      )}
                    </div>
                    <Can cap="warehouse.operate">
                      <button
                        disabled={busyId !== null}
                        onClick={() => reassign(o)}
                        className="shrink-0 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        {busyId === o.order_id ? 'Asignando…' : 'Elegir esta venta'}
                      </button>
                    </Can>
                  </div>
                  <div className="text-xs">
                    {o.purchases.length === 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                        <ShoppingCart size={12} /> Sin compra Amazon asociada
                      </span>
                    ) : (
                      <div className="space-y-1">
                        {o.purchases.map((p) => (
                          <div
                            key={p.id}
                            className="inline-flex flex-wrap items-center gap-1.5 px-2 py-1 rounded bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
                          >
                            <ShoppingCart size={12} />
                            <span>
                              Ya tiene compra {PURCHASE_SOURCE[p.link_source || ''] || p.link_source || ''}
                              {p.amazon_order_id ? ` ${p.amazon_order_id}` : ''} · estado {p.amazon_status}
                              {p.tracking_number ? ` · seguimiento ${p.tracking_number}` : ' · sin seguimiento'}
                            </span>
                            <span className="opacity-70">→ quedará reemplazada</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
